// Account page for Sunlight Grounds Care Client Portal
import { useState, useEffect, useCallback } from 'react';
import api from '@/api/client';
import { useProperty } from '@/hooks/useProperty';
import { useAuth } from '@/hooks/useAuth';
import { openCheckoutSession, openCustomerPortal } from '@/api/stripe';
import { Bell, ChevronRight, CreditCard, Plus, RefreshCw, FileText, Download, ExternalLink } from 'lucide-react';

export default function Account({ user }) {
  const { selectedProperty } = useProperty();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [billing, setBilling] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [showPropertyPanel, setShowPropertyPanel] = useState(false);

  const loadData = useCallback(async (showRefresh = false) => {
    if (!selectedProperty) {
      setLoading(false);
      return;
    }
    
    if (showRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const results = await Promise.allSettled([
        api.getPropertyInfo(selectedProperty.id),
        api.getServices(selectedProperty.id),
        api.getInvoices(selectedProperty.id)
      ]);
      
      if (results[0].status === 'fulfilled') setPropertyInfo(results[0].value);
      else setPropertyInfo(null);
      if (results[1].status === 'fulfilled') {
        const svc = results[1].value;
        const all = Array.isArray(svc) ? svc : [];
        setServices(all.filter(s => !s.is_default));
      } else setServices([]);
      if (results[2].status === 'fulfilled') {
        const billData = results[2].value;
        setBilling({
          invoices: billData.invoices || billData || [],
          total_due: billData.total_due || 0,
          count: billData.count || 0,
        });
      } else setBilling(null);
    } catch (err) {
      console.error('Failed to load account data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedProperty]);

  // Reset stale data immediately when property changes
  useEffect(() => {
    setPropertyInfo(null);
    setServices([]);
    setBilling(null);
    setShowPropertyPanel(false);
    setLoading(true);
  }, [selectedProperty?.id]);

  useEffect(() => {
    loadData();
    
    // Poll for new invoices every 30 seconds
    const interval = setInterval(() => {
      loadData(false);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loadData]);

  const getInitials = (name) => {
    if (!name) return 'SG';
    const parts = name.split(' ');
    return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
  };

  const handlePayNow = async (invoice) => {
    // If invoice has a hosted URL from Stripe, open that directly
    if (invoice.hosted_invoice_url) {
      window.open(invoice.hosted_invoice_url, '_blank');
      return;
    }
    
    // Otherwise pay via backend
    setPayingInvoice(invoice.id);
    try {
      const result = await api.payInvoice(invoice.id);
      if (result.checkout_url) {
        window.open(result.checkout_url, '_blank');
      } else if (result.payment_url) {
        window.open(result.payment_url, '_blank');
      }
    } catch (err) {
      console.error('Failed to pay invoice:', err);
    } finally {
      setPayingInvoice(null);
    }
  };

  const handleViewInvoice = (invoice) => {
    if (invoice.hosted_invoice_url) {
      window.open(invoice.hosted_invoice_url, '_blank');
    }
  };

  const handleDownloadPdf = (invoice) => {
    if (invoice.invoice_pdf) {
      window.open(invoice.invoice_pdf, '_blank');
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      const { portal_url } = await api.getBillingPortal();
      openCustomerPortal(portal_url);
    } catch (err) {
      console.error('Failed to open billing portal:', err);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      const { portal_url } = await api.getBillingPortal();
      openCustomerPortal(portal_url);
    } catch (err) {
      console.error('Failed to add payment method:', err);
    }
  };

  const handleRefresh = () => {
    loadData(true);
  };

  // Map Stripe invoice statuses to display
  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="inline-block text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-green-light text-green">Paid</span>;
      case 'open':
      case 'due':
        return <span className="inline-block text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">Due</span>;
      case 'draft':
        return <span className="inline-block text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate">Draft</span>;
      case 'pending':
      case 'uncollectible':
        return <span className="inline-block text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-gold-light text-[#8B6A10]">Pending</span>;
      case 'void':
        return <span className="inline-block text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate">Void</span>;
      default:
        return <span className="inline-block text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate">{status}</span>;
    }
  };

  const isPayable = (status) => {
    return status === 'open' || status === 'due';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-light">
        <header className="bg-navy pb-4">
          <div className="flex items-center px-5 py-3">
            <img src="/images/logo-white.png" alt="Sunlight Grounds Care" className="h-10 w-auto" />
          </div>
        </header>
        <div className="bg-navy px-5 py-4">
          <div className="animate-pulse flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white/20"></div>
            <div className="h-5 bg-white/20 rounded w-32"></div>
          </div>
        </div>
        <div className="p-4">
          <div className="sg-card p-4 animate-pulse mb-4">
            <div className="h-3 bg-slate/20 rounded w-24 mb-3"></div>
            <div className="h-3 bg-slate/20 rounded w-40 mb-2"></div>
            <div className="h-3 bg-slate/20 rounded w-32"></div>
          </div>
          <button
            onClick={logout}
            className="w-full bg-slate-light text-slate font-dm text-xs font-medium py-3 rounded-xl
                       hover:bg-slate/20 transition-colors mt-4 border border-black/[0.06]"
            data-testid="logout-btn-loading"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-light pb-24" data-testid="account-page">
      {/* Header with logo only */}
      <header className="bg-navy px-5 pt-3 pb-0" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="flex items-center justify-between">
          <img src="/images/logo-white.png" alt="Sunlight Grounds Care" className="h-10 w-auto" />
          <button className="p-2 -mr-2 text-white/70 hover:text-white transition-colors">
            <Bell size={20} />
          </button>
        </div>
      </header>

      {/* Account Hero */}
      <div className="bg-navy px-5 py-4" data-testid="account-hero">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gold flex items-center justify-center">
            <span className="font-dm text-[15px] font-bold text-navy">
              {getInitials(user?.name || selectedProperty?.name)}
            </span>
          </div>
          <div>
            <h1 className="font-cormorant text-lg font-semibold text-white">{user?.name || selectedProperty?.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-dm text-[10px] text-white/55">Premium Plan</span>
              <span className="bg-gold text-navy text-[9px] font-dm font-medium px-2 py-0.5 rounded-full">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 md:p-8 max-w-4xl mx-auto">
        {/* Property Details */}
        <p className="sg-label mb-2">Property details</p>
        <div className="sg-card mb-4" data-testid="property-details-card">
          <div className="sg-info-row">
            <span className="text-slate">Address</span>
            <span className="font-medium text-navy text-right text-xs max-w-[60%]">
              {propertyInfo?.address || selectedProperty?.address || '—'}
            </span>
          </div>
          <div className="sg-info-row">
            <span className="text-slate">Property type</span>
            <span className="font-medium text-navy capitalize">{propertyInfo?.property_type || selectedProperty?.property_type || '—'}</span>
          </div>
          <div className="sg-info-row">
            <span className="text-slate">Service line</span>
            <span className="font-medium text-navy capitalize">{propertyInfo?.service_line || '—'}</span>
          </div>
          <div className="sg-info-row">
            <span className="text-slate">Gate code</span>
            <span className="font-medium text-navy">{propertyInfo?.access_info?.gate_code || '—'}</span>
          </div>
          <div className="sg-info-row border-b-0">
            <span className="text-slate">Member since</span>
            <span className="font-medium text-navy">
              {propertyInfo?.created_at 
                ? new Date(propertyInfo.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                : '—'}
            </span>
          </div>
        </div>

        {/* Active Services */}
        <p className="sg-label pt-2 mb-2">Active services</p>
        <div className="sg-card p-3 mb-4" data-testid="active-services-card">
          <div className="flex flex-wrap gap-2">
            {services.map((service, idx) => (
              <span 
                key={idx}
                className="bg-green-light text-green text-[10px] font-dm font-bold px-3 py-1.5 rounded-full"
              >
                {service.name} · {service.frequency}
              </span>
            ))}
          </div>
        </div>

        {/* Billing / Invoices */}
        <div className="flex items-center justify-between pt-2 mb-2">
          <p className="sg-label">Invoices</p>
          <button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 text-[10px] text-navy-mid font-dm hover:text-navy transition-colors disabled:opacity-50"
            data-testid="refresh-invoices-btn"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        
        <div className="sg-card mb-4" data-testid="billing-card">
          {/* Payment Method */}
          {billing?.payment_method && (
            <button
              onClick={handleOpenBillingPortal}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-light/50 transition-colors border-b border-black/[0.06]"
              data-testid="payment-method-row"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-navy-light flex items-center justify-center">
                  <CreditCard size={16} className="text-navy" />
                </div>
                <div>
                  <p className="font-dm text-[11px] font-medium text-navy">
                    {billing.payment_method.brand} •••• {billing.payment_method.last4}
                  </p>
                  <p className="font-dm text-[9px] text-slate">
                    Expires {billing.payment_method.exp} · Default
                  </p>
                </div>
              </div>
              <ChevronRight size={14} className="text-slate" />
            </button>
          )}

          {/* Add Payment Method Button */}
          <button
            onClick={handleAddPaymentMethod}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-navy-mid border-b border-black/[0.06] hover:bg-slate-light/30 transition-colors"
            data-testid="add-payment-method-btn"
          >
            <Plus size={14} />
            <span className="font-dm text-[11px]">Add payment method</span>
          </button>

          {/* Invoice List */}
          {billing?.invoices?.length > 0 ? (
            <>
              {billing.invoices.map((invoice, idx) => (
                <div 
                  key={invoice.id || idx}
                  className={`px-4 py-3 ${
                    idx < billing.invoices.length - 1 ? 'border-b border-black/[0.06]' : ''
                  }`}
                  data-testid={`invoice-row-${invoice.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-dm text-xs font-bold text-navy">{invoice.service}</p>
                      {invoice.description && (
                        <p className="font-dm text-[10px] text-slate mt-0.5 line-clamp-1">
                          {invoice.description}
                        </p>
                      )}
                      <p className="font-dm text-[10px] text-slate mt-0.5">
                        {invoice.stripe_invoice_id?.substring(0, 12)}... · {invoice.date}
                      </p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="font-cormorant text-[15px] font-semibold text-navy">
                        ${invoice.amount?.toLocaleString()}
                      </p>
                      {getStatusBadge(invoice.status)}
                    </div>
                  </div>
                  
                  {/* Action buttons for invoices */}
                  <div className="flex items-center gap-2 mt-2">
                    {isPayable(invoice.status) && (
                      <button
                        onClick={() => handlePayNow(invoice)}
                        disabled={payingInvoice === invoice.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-navy text-white rounded-lg text-[10px] font-dm font-medium hover:bg-navy-mid transition-colors disabled:opacity-50"
                        data-testid={`pay-now-${invoice.id}`}
                      >
                        {payingInvoice === invoice.id ? 'Processing...' : 'Pay now'}
                      </button>
                    )}
                    
                    {invoice.hosted_invoice_url && (
                      <button
                        onClick={() => handleViewInvoice(invoice)}
                        className="flex items-center gap-1 px-2 py-1.5 text-navy-mid rounded-lg text-[10px] font-dm hover:bg-slate-light transition-colors"
                        data-testid={`view-invoice-${invoice.id}`}
                      >
                        <ExternalLink size={12} />
                        View
                      </button>
                    )}
                    
                    {invoice.invoice_pdf && (
                      <button
                        onClick={() => handleDownloadPdf(invoice)}
                        className="flex items-center gap-1 px-2 py-1.5 text-navy-mid rounded-lg text-[10px] font-dm hover:bg-slate-light transition-colors"
                        data-testid={`download-pdf-${invoice.id}`}
                      >
                        <Download size={12} />
                        PDF
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Stripe Security Badge */}
              <div className="flex items-center justify-center gap-1.5 px-4 py-2 border-t border-black/[0.06]">
                <span className="font-dm text-[9px] text-slate">Payments secured by</span>
                <span className="font-dm text-[10px] font-medium text-[#635BFF]">stripe</span>
              </div>
            </>
          ) : (
            <div className="px-4 py-8 text-center">
              <FileText size={32} className="text-slate/40 mx-auto mb-2" />
              <p className="font-dm text-xs text-slate">No invoices yet</p>
              <p className="font-dm text-[10px] text-slate/60 mt-1">
                Invoices will appear here when created
              </p>
            </div>
          )}
        </div>

        {/* Settings */}
        <p className="sg-label pt-2 mb-2">Settings</p>
        <div className="sg-card mb-4" data-testid="settings-card">
          {[
            { icon: '🔔', label: 'Notifications', subtitle: 'Service reminders, messages', action: 'toggle' },
            { icon: '📍', label: 'Property info', subtitle: 'Address, access notes', action: 'property-info' },
            { icon: '💳', label: 'Payment method', subtitle: billing?.payment_method ? `${billing.payment_method.brand} •••• ${billing.payment_method.last4}` : 'Add a card', action: 'stripe' },
            { icon: '📞', label: 'Contact support', subtitle: '(626) 737-2797', action: 'call', isSupport: true }
          ].map((item, idx) => (
            <button
              key={idx}
              onClick={() => {
                if (item.action === 'call') {
                  window.location.href = 'tel:+16267372797';
                } else if (item.action === 'stripe') {
                  handleOpenBillingPortal();
                } else if (item.action === 'property-info') {
                  setShowPropertyPanel(true);
                }
              }}
              className={`
                w-full flex items-center justify-between px-4 py-3 text-left
                hover:bg-slate-light/50 transition-colors
                ${idx < 3 ? 'border-b border-black/[0.06]' : ''}
              `}
              data-testid={`settings-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${item.isSupport ? 'bg-red-50' : 'bg-navy-light'}`}>
                  {item.icon}
                </div>
                <div>
                  <p className={`font-dm text-xs font-bold ${item.isSupport ? 'text-red-700' : 'text-navy'}`}>
                    {item.label}
                  </p>
                  <p className="font-dm text-[10px] text-slate">{item.subtitle}</p>
                </div>
              </div>
              <ChevronRight size={14} className="text-slate" />
            </button>
          ))}
        </div>

        {/* Logout Button */}
        <button
          onClick={logout}
          className="w-full bg-slate-light text-slate font-dm text-xs font-medium py-3 rounded-xl
                     hover:bg-slate/20 transition-colors mt-4"
          data-testid="logout-btn"
        >
          Sign out
        </button>
      </div>

      {/* Property Info Panel */}
      {showPropertyPanel && (
        <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowPropertyPanel(false)}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto animate-slide-up"
            onClick={e => e.stopPropagation()}
            data-testid="property-info-panel"
          >
            <div className="sticky top-0 bg-white px-5 pt-4 pb-2 border-b border-black/[0.06]">
              <div className="w-10 h-1 bg-slate/20 rounded-full mx-auto mb-3"></div>
              <h3 className="font-cormorant text-lg font-bold text-navy">Property Info</h3>
              <p className="font-dm text-[10px] text-slate mt-0.5">
                {propertyInfo?.name || selectedProperty?.name} — {propertyInfo?.address || selectedProperty?.address}
              </p>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Access Info */}
              <div>
                <p className="sg-label mb-2">Access information</p>
                <div className="sg-card">
                  {[
                    { label: 'Gate code', value: propertyInfo?.access_info?.gate_code },
                    { label: 'Key location', value: propertyInfo?.access_info?.key_location },
                    { label: 'Parking', value: propertyInfo?.access_info?.parking },
                    { label: 'Special notes', value: propertyInfo?.access_info?.special_notes },
                  ].filter(item => item.value).map((item, idx, arr) => (
                    <div key={idx} className={`sg-info-row ${idx === arr.length - 1 ? 'border-b-0' : ''}`}>
                      <span className="text-slate">{item.label}</span>
                      <span className="font-medium text-navy text-right max-w-[60%] text-xs">{item.value}</span>
                    </div>
                  ))}
                  {!propertyInfo?.access_info?.gate_code && !propertyInfo?.access_info?.key_location && 
                   !propertyInfo?.access_info?.parking && !propertyInfo?.access_info?.special_notes && (
                    <div className="px-4 py-3 text-center">
                      <p className="font-dm text-xs text-slate">No access info added yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Service Specifications */}
              {propertyInfo?.service_specifications && (
                <div>
                  <p className="sg-label mb-2">Service specifications</p>
                  <div className="sg-card">
                    {propertyInfo.service_specifications.mowing_height && (
                      <div className="sg-info-row">
                        <span className="text-slate">Mowing height</span>
                        <span className="font-medium text-navy">{propertyInfo.service_specifications.mowing_height}</span>
                      </div>
                    )}
                    <div className="sg-info-row">
                      <span className="text-slate">Chemicals allowed</span>
                      <span className="font-medium text-navy">
                        {propertyInfo.service_specifications.chemicals_allowed ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="sg-info-row border-b-0">
                      <span className="text-slate">Organic only</span>
                      <span className="font-medium text-navy">
                        {propertyInfo.service_specifications.organic_only ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Property Notes */}
              {propertyInfo?.notes && (
                <div>
                  <p className="sg-label mb-2">Notes</p>
                  <div className="sg-card p-4">
                    <p className="font-dm text-xs text-navy whitespace-pre-wrap">{propertyInfo.notes}</p>
                  </div>
                </div>
              )}

              {/* Irrigation Schedule */}
              {propertyInfo?.irrigation_schedule && (
                <div>
                  <p className="sg-label mb-2">Irrigation schedule</p>
                  <div className="sg-card p-4">
                    <p className="font-dm text-xs text-navy">{propertyInfo.irrigation_schedule}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 pt-0">
              <button
                onClick={() => setShowPropertyPanel(false)}
                className="w-full bg-navy text-white font-dm text-xs font-medium py-3 rounded-xl"
                data-testid="close-property-panel"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
